import { GALLERY_IMAGES, CLOSING_PHOTO, TROUPE_PHOTO } from "./constants";
import { edition as galleryEdition, src as photoSrc } from "./gallery";
import posters from "../data/posters.json";

// ── Editions ────────────────────────────────────────────────────────────
// Every Antara edition so far, newest first. `shows` is how many shows the
// edition had (30 August ran a matinee and an evening), so the "shows so far"
// count is the sum, never typed by hand.
//
// Each photo is { src, alt, width, height }: the edition pages render them
// with real dimensions (no layout shift) and real alt text.
//
// Setlists are still unknown rather than filler; the page says "coming soon".
const P = process.env.PUBLIC_URL || "";

// Photos from the gallery manifest — featured frames first.
function galleryPhotos(id, n = 8) {
  const ed = galleryEdition(id);
  if (!ed) return [];
  const list = [...ed.photos.filter((p) => p.featured), ...ed.photos.filter((p) => !p.featured)];
  return list.slice(0, n).map((ph) => ({
    src: photoSrc({ ...ph, edition: { id } }, 1200),
    alt: ph.alt,
    width: ph.width,
    height: ph.height,
  }));
}

// Older images that are not in the gallery manifest; sizes are the files'.
const LEGACY = {
  [GALLERY_IMAGES[0]]: [1066, 1600], [GALLERY_IMAGES[1]]: [1400, 2100],
  [GALLERY_IMAGES[3]]: [1400, 933], [GALLERY_IMAGES[4]]: [1400, 933],
  [GALLERY_IMAGES[5]]: [1400, 933], [GALLERY_IMAGES[6]]: [1400, 933],
  [CLOSING_PHOTO]: [1200, 600], [TROUPE_PHOTO]: [1600, 900],
};
const legacy = (src, alt) => ({ src, alt, width: LEGACY[src][0], height: LEGACY[src][1] });

const posterPhoto = (name, alt) => {
  const p = posters.posters[name];
  const w = p.widths[p.widths.length - 1];
  return { src: `${P}/posters/${name}-${w}.jpg`, alt, width: w, height: Math.round((w * p.height) / p.width) };
};

export const EDITIONS = [
  {
    slug: "25-october-2026",
    dateLabel: "25 October 2026",
    dateISO: "2026-10-25",
    city: "Chandigarh",
    venue: "Tagore Theatre, Sector 18",
    title: "Bhaav Sangam",
    status: "past",
    soldOut: true,
    shows: 1,
    events: [{ start: "2026-10-25T19:00:00+05:30" }],
    address: { name: "Tagore Theatre", streetAddress: "Sector 18-B", addressLocality: "Chandigarh", postalCode: "160018", addressRegion: "Chandigarh" },
    galleryId: "chandigarh-2026-10",
    tagline: "Antara's first Chandigarh edition — sold out.",
    seoDesc: "Antara's first Chandigarh edition: Bhaav Sangam, live bhajan jamming with Anshu Goyal at Tagore Theatre, Sector 18 on 25 October 2026. Sold out. Photos and recap.",
    recap:
      "Bhaav Sangam came to Chandigarh's Tagore Theatre on 25 October 2026 — Antara's first evening in the city, presented with Catapultt Events, and sold out ahead of the night. Anshu Goyal opened from the sofa with stories of Kashi, the band holding the groove beneath screens of the ghats, Shiva and Krishna. By nine the stalls were on their feet, and she came down to the edge of the stage to sing with the front rows before the hall settled back into stillness for the close.",
    photos: galleryPhotos("chandigarh-2026-10"),
    setlist: [],
  },
  {
    slug: "23-october-2026",
    dateLabel: "23 October 2026",
    dateISO: "2026-10-23",
    city: "New Delhi",
    venue: "The Grand, Vasant Kunj",
    title: "Bhaav Sangam",
    status: "past",
    soldOut: true,
    shows: 1,
    events: [{ start: "2026-10-23T19:30:00+05:30" }],
    address: { name: "The Grand New Delhi", streetAddress: "Nelson Mandela Road, Vasant Kunj Phase 2", addressLocality: "New Delhi", postalCode: "110070", addressRegion: "Delhi" },
    galleryId: "delhi-2026-10",
    tagline: "Bhaav Sangam under the open sky at The Grand — sold out.",
    seoDesc: "Bhaav Sangam at The Grand, Vasant Kunj on 23 October 2026: a sold-out, open-air evening of live bhajan jamming with Anshu Goyal and Antara. Photos and recap.",
    recap:
      "Bhaav Sangam, presented with Catapultt Events, took the lawns of The Grand in Vasant Kunj on 23 October 2026 and sold out. Vanisha Fitkariwala opened the night before Anshu Goyal and the Antara band took the stage beneath giant screens of Ganesha, Shiva, Ram and Krishna. By the second half the front of the lawn was on its feet, hands in the air, singing every refrain back.",
    photos: galleryPhotos("delhi-2026-10"),
    setlist: [],
  },
  {
    slug: "30-august-2026",
    dateLabel: "30 August 2026",
    dateISO: "2026-08-30",
    city: "New Delhi",
    venue: "M.P.C.U. Shah Auditorium, Raj Niwas Marg, Civil Lines",
    status: "past",
    shows: 2,
    events: [
      { start: "2026-08-30T15:00:00+05:30", end: "2026-08-30T17:00:00+05:30", label: "Matinee" },
      { start: "2026-08-30T19:00:00+05:30", end: "2026-08-30T21:00:00+05:30", label: "Evening" },
    ],
    address: { name: "M.P.C.U. Shah Auditorium", streetAddress: "Raj Niwas Marg, Civil Lines", addressLocality: "New Delhi", postalCode: "110054", addressRegion: "Delhi" },
    galleryId: "delhi-2026-08",
    tagline: "Antara's first Delhi edition — two shows in a single day.",
    seoDesc: "Antara's first Delhi edition: a matinee and an evening of live bhajan jamming at M.P.C.U. Shah Auditorium, Civil Lines on 30 August 2026. Photos and recap.",
    recap:
      "Antara's first Delhi edition took M.P.C.U. Shah Auditorium in Civil Lines for a matinee and an evening show on the same day, around 1,500 people across the two. The balcony filled, the front rows stood, and by the closing bhajan the hall was singing the refrains back louder than the band.",
    photos: galleryPhotos("delhi-2026-08", 4),
    setlist: [],
  },
  {
    slug: "11-july-2026",
    dateLabel: "11 July 2026",
    dateISO: "2026-07-11",
    city: "Gurgaon",
    venue: "American Excelsior Auditorium, Sector 43",
    status: "past",
    shows: 1,
    events: [{ start: "2026-07-11" }],
    address: { name: "American Excelsior Auditorium", streetAddress: "Sector 43", addressLocality: "Gurugram", addressRegion: "Haryana" },
    tagline: "The Gurgaon edition that set up Antara's move to Delhi.",
    seoDesc: "Antara in Gurgaon on 11 July 2026: a houseful evening of live bhajan jamming at American Excelsior Auditorium, Sector 43 — the night that led to Delhi.",
    recap:
      "Another houseful Gurgaon evening. The energy in the room made the case for a bigger stage — and pointed Antara toward its first Delhi edition.",
    photos: [GALLERY_IMAGES[4], GALLERY_IMAGES[5], GALLERY_IMAGES[6], TROUPE_PHOTO].map((src) => legacy(src, "Antara — 11 July 2026, Gurgaon")),
    setlist: [],
  },
  {
    slug: "30-may-2026",
    dateLabel: "30 May 2026",
    dateISO: "2026-05-30",
    city: "Gurgaon",
    venue: "American Excelsior Auditorium, Sector 43",
    status: "past",
    shows: 1,
    events: [{ start: "2026-05-30" }],
    address: { name: "American Excelsior Auditorium", streetAddress: "Sector 43", addressLocality: "Gurugram", addressRegion: "Haryana" },
    tagline: "An early Antara evening in Gurgaon.",
    seoDesc: "Antara in Gurgaon on 30 May 2026: an early evening of live bhajan jamming at American Excelsior Auditorium, Sector 43, where the audience became the choir.",
    recap:
      "One of Antara's Gurgaon evenings — a full room, a live band, and a crowd that arrived as an audience and left as a choir.",
    photos: [GALLERY_IMAGES[0], GALLERY_IMAGES[1], GALLERY_IMAGES[3], CLOSING_PHOTO].map((src) => legacy(src, "Antara — 30 May 2026, Gurgaon")),
    setlist: [],
  },
];

/** Shows played so far: every edition's `shows`, summed. */
export const SHOWS_SO_FAR = EDITIONS.filter((e) => e.status === "past").reduce((n, e) => n + (e.shows || 1), 0);

export function getEdition(slug) {
  return EDITIONS.find((e) => e.slug === slug);
}

/** MusicEvent JSON-LD for an edition's shows (past events, so no offers). */
export function editionJsonLd(e, url) {
  return e.events.map((ev) => ({
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    name: `${e.title ? `${e.title}: ` : "Antara — "}Bhajan Jamming, ${e.city}${ev.label ? ` (${ev.label})` : ""}`,
    description: e.recap,
    startDate: ev.start,
    ...(ev.end ? { endDate: ev.end } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    url,
    image: e.photos.slice(0, 3).map((ph) => `https://bhajanjamming.com${ph.src.replace(/^https?:\/\/[^/]+/, "")}`),
    performer: [{ "@type": "MusicGroup", name: "Antara" }, { "@type": "Person", name: "Anshu Goyal" }],
    organizer: { "@type": "Organization", name: e.title ? "Catapultt Events" : "Kashi with Anshu" },
    location: {
      "@type": "Place",
      name: e.address.name,
      address: { "@type": "PostalAddress", ...Object.fromEntries(Object.entries(e.address).filter(([k]) => k !== "name")), addressCountry: "IN" },
    },
  }));
}
